/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — canvas overlays: remote multiplayer cursors + snap
   alignment guide lines. Both render inside the React Flow viewport
   and transform flow-space coordinates to screen-space via the live
   viewport, ported behavior-identical from the old OrgChart.tsx.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { useViewport } from '@xyflow/react';
import type { RemoteCursor } from './hooks/useMultiplayer';
import { proximitySnapGapRef } from './hooks/useNodeDragAndSnap';
import { rem } from '../../../lib/rem';

function userColor(userId: string): string {
  let h = 0;
  for (let i = 0; i < userId.length; i++) {
    h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return `hsl(${h % 360}, 65%, 50%)`;
}

export function RemoteCursors({ cursors }: { cursors: Record<string, RemoteCursor> }) {
  const { x: vpX, y: vpY, zoom } = useViewport();
  const entries = Object.entries(cursors);
  if (entries.length === 0) return null;
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 100,
        overflow: 'hidden',
      }}
    >
      {entries.map(([userId, c]) => {
        const screenX = c.x * zoom + vpX;
        const screenY = c.y * zoom + vpY;
        const color = userColor(userId);
        return (
          <div
            key={userId}
            style={{
              position: 'absolute',
              left: screenX,
              top: screenY,
              transition: 'left 80ms linear, top 80ms linear',
              willChange: 'left, top',
            }}
          >
            <svg
              width="20"
              height="22"
              viewBox="0 0 20 22"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))', display: 'block' }}
            >
              <path
                d="M 2,2 L 2,17 L 6.5,13 L 9.5,20 L 11.5,19 L 8.5,12 L 15,12 Z"
                fill={color}
                stroke="white"
                strokeWidth="1.5"
              />
            </svg>
            <div
              style={{
                background: color,
                color: 'white',
                padding: rem(2) + ' var(--space-2)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                marginTop: rem(2),
                marginLeft: 'var(--space-1)',
                whiteSpace: 'nowrap',
                boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                letterSpacing: '-0.005em',
              }}
            >
              {c.userName}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function SnapGuideLines({ hY, vX }: { hY?: number; vX?: number }) {
  const { x: vpX, y: vpY, zoom } = useViewport();
  // Proximity gap indicator (Josh grant 2026-08-18) — read directly
  // from the hook's module-scoped ref rather than a prop; see the
  // ref's doc comment in useNodeDragAndSnap.ts for why. Re-render is
  // driven by the parent's hY/vX state update, which the hook sets on
  // the same drag frame it writes this ref, so the value read here is
  // always current.
  const gap = proximitySnapGapRef.current;
  if (hY === undefined && vX === undefined && !gap) return null;
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 100,
        overflow: 'hidden',
      }}
    >
      {hY !== undefined && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: hY * zoom + vpY,
            height: rem(1),
            borderTop: '1px dashed var(--ac)',
            opacity: 0.6,
          }}
        />
      )}
      {vX !== undefined && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: vX * zoom + vpX,
            width: rem(1),
            borderLeft: '1px dashed var(--ac)',
            opacity: 0.6,
          }}
        />
      )}
      {gap && <ProximityGapTick gap={gap} vpX={vpX} vpY={vpY} zoom={zoom} />}
    </div>
  );
}

/** Small perpendicular tick + gap-size label between two proximity-
 *  snapped cards — subtle, accent token, no text weight. Kept as its
 *  own component purely for readability; not exported (internal to
 *  the guide overlay). */
function ProximityGapTick({
  gap,
  vpX,
  vpY,
  zoom,
}: {
  gap: { axis: 'vertical' | 'horizontal'; cross: number; from: number; to: number };
  vpX: number;
  vpY: number;
  zoom: number;
}) {
  const mid = (gap.from + gap.to) / 2;
  const gapSize = Math.round(Math.abs(gap.to - gap.from));
  const tickHalf = 5;
  if (gap.axis === 'vertical') {
    const midScreenY = mid * zoom + vpY;
    const crossScreenX = gap.cross * zoom + vpX;
    return (
      <>
        <div
          style={{
            position: 'absolute',
            left: crossScreenX - tickHalf,
            top: midScreenY,
            width: tickHalf * 2,
            height: 0,
            borderTop: '1px dashed var(--ac)',
            opacity: 0.8,
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: crossScreenX + tickHalf + 4,
            top: midScreenY - 6,
            fontSize: 'var(--font-size-xs)',
            color: 'var(--tm)',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {gapSize}
        </div>
      </>
    );
  }
  const midScreenX = mid * zoom + vpX;
  const crossScreenY = gap.cross * zoom + vpY;
  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: crossScreenY - tickHalf,
          left: midScreenX,
          height: tickHalf * 2,
          width: 0,
          borderLeft: '1px dashed var(--ac)',
          opacity: 0.8,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: crossScreenY + tickHalf + 2,
          left: midScreenX - 6,
          fontSize: 'var(--font-size-xs)',
          color: 'var(--tm)',
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {gapSize}
      </div>
    </>
  );
}
